'use client';
import { useEffect, useMemo } from 'react';
import {
    DISPLAY_FONTS,
    normalizeGoogleFontWeights as normalizeWeights,
    sanitizeFontFamily,
    TEXT_FONTS,
} from '@/utils/google-fonts';

export { DISPLAY_FONTS, TEXT_FONTS };
const createLink = (fonts, doc) => {
    const families = fonts
        .reduce((acc, font) => {
            const family = String(font.font || '')
                .trim()
                .replace(/ +/g, '+');
            const weights = (font.weights || []).join(';');
            return [...acc, family + (weights && `:wght@${weights}`)];
        }, [])
        .join('&family=');
    if (!families) return null;
    const link = doc.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${families}&display=swap`;
    link.dataset.editorFontLoader = 'true';
    return link;
};

const normalizeExtraFontEntry = (entry) => {
    if (typeof entry === 'string') {
        const font = sanitizeFontFamily(entry);
        return font ? { font, weights: [400] } : null;
    }
    if (!entry || typeof entry !== 'object') return null;
    const font = sanitizeFontFamily(entry.font);
    if (!font) return null;
    const weights = normalizeWeights(entry.weights || [400]);
    return { font, weights: weights.length ? weights : [400] };
};

const cssFontFormat = (format) => {
    if (format === 'ttf') return 'truetype';
    if (format === 'otf') return 'opentype';
    return format === 'woff' ? 'woff' : 'woff2';
};

const sanitizeUploadedFontFamily = (family) =>
    sanitizeFontFamily(family)
        .replace(/[^A-Za-z0-9 _.-]/g, '')
        .trim();

const getFontList = (
    extraFonts = [],
    uploadedFonts = [],
    includeCatalog = true
) => {
    const fonts = includeCatalog ? [...DISPLAY_FONTS] : [];
    if (includeCatalog) {
        TEXT_FONTS.forEach((font) => {
            if (!fonts.find((f) => f.font === font.font)) {
                fonts.push(font);
            }
        });
    }

    const seen = new Set(fonts.map((font) => font.font.toLowerCase()));
    extraFonts.forEach((entry) => {
        const normalized = normalizeExtraFontEntry(entry);
        if (!normalized) return;
        const key = normalized.font.toLowerCase();
        if (seen.has(key)) return;
        seen.add(key);
        fonts.push(normalized);
    });

    const uploadedFamilies = new Set(
        uploadedFonts.map((font) =>
            sanitizeUploadedFontFamily(font?.family).toLowerCase()
        )
    );
    return fonts.filter(
        (font) => !uploadedFamilies.has(font.font.toLowerCase())
    );
};

export const GoogleFontLoader = ({
    extraFonts = [],
    uploadedFonts = [],
    targetDocument,
    includeCatalog = true,
    onReady,
}) => {
    const extraFontsKey = JSON.stringify(extraFonts || []);
    const sanitizedExtraFonts = useMemo(() => {
        try {
            const parsed = JSON.parse(extraFontsKey);
            const list = Array.isArray(parsed) ? parsed : [];
            const byFamily = new Map();
            list.forEach((entry) => {
                const normalized = normalizeExtraFontEntry(entry);
                if (!normalized) return;
                const key = normalized.font.toLowerCase();
                const current = byFamily.get(key);
                if (!current) {
                    byFamily.set(key, normalized);
                    return;
                }
                byFamily.set(key, {
                    font: normalized.font,
                    weights: normalizeWeights([
                        ...current.weights,
                        ...normalized.weights,
                    ]),
                });
            });
            return Array.from(byFamily.values());
        } catch {
            return [];
        }
    }, [extraFontsKey]);

    useEffect(() => {
        const doc =
            targetDocument ||
            (typeof document !== 'undefined' ? document : null);
        if (!doc) return undefined;

        let cancelled = false;
        const fonts = getFontList(
            sanitizedExtraFonts,
            uploadedFonts,
            includeCatalog
        );
        const link = createLink(fonts, doc);
        const style = doc.createElement('style');
        style.dataset.editorFontLoader = 'true';
        style.dataset.uploadedFonts = 'true';
        style.textContent = uploadedFonts
            .filter(
                (font) =>
                    sanitizeUploadedFontFamily(font?.family) &&
                    typeof font?.url === 'string' &&
                    font.url.startsWith('/fonts/')
            )
            .map((font) => {
                const source =
                    typeof font.previewUrl === 'string' &&
                    font.previewUrl.startsWith('data:')
                        ? font.previewUrl
                        : font.url;
                return `@font-face { font-family: "${sanitizeFontFamily(
                    sanitizeUploadedFontFamily(font.family)
                )}"; src: url("${String(source).replace(
                    /["'<>]/g,
                    ''
                )}") format("${cssFontFormat(font.format)}"); font-weight: ${
                    font.weight || 400
                }; font-style: ${
                    font.style === 'italic' ? 'italic' : 'normal'
                }; font-display: swap; }`;
            })
            .join('\n');
        if (link) doc.head.appendChild(link);
        if (style.textContent) doc.head.appendChild(style);

        const waitForLink = link
            ? new Promise((resolve) => {
                  let timeoutId = null;
                  const done = () => {
                      if (timeoutId !== null) {
                          window.clearTimeout(timeoutId);
                      }
                      link.removeEventListener('load', done);
                      link.removeEventListener('error', done);
                      resolve();
                  };

                  if (link.sheet) {
                      resolve();
                      return;
                  }

                  link.addEventListener('load', done, { once: true });
                  link.addEventListener('error', done, { once: true });
                  timeoutId = window.setTimeout(done, 3000);
              })
            : Promise.resolve();

        const signalReady = async () => {
            await waitForLink;

            if (doc.fonts?.load) {
                const requestedFaces = [
                    ...fonts.flatMap((font) =>
                        (font.weights || [400]).map(
                            (weight) =>
                                `normal ${weight} 1em "${sanitizeFontFamily(
                                    font.font
                                )}"`
                        )
                    ),
                    ...uploadedFonts.map(
                        (font) =>
                            `${
                                font?.style === 'italic' ? 'italic' : 'normal'
                            } ${font?.weight || 400} 1em "${sanitizeFontFamily(
                                sanitizeUploadedFontFamily(font?.family)
                            )}"`
                    ),
                ];

                await Promise.race([
                    Promise.allSettled(
                        requestedFaces.map((face) => doc.fonts.load(face))
                    ),
                    new Promise((resolve) => window.setTimeout(resolve, 3000)),
                ]);
            }

            if (!cancelled) onReady?.();
        };

        signalReady();

        return () => {
            cancelled = true;
            if (link?.parentNode) {
                link.parentNode.removeChild(link);
            }
            if (style.parentNode) style.parentNode.removeChild(style);
        };
    }, [
        includeCatalog,
        onReady,
        sanitizedExtraFonts,
        targetDocument,
        uploadedFonts,
    ]);
    return null;
};
